import { useState } from "react";
import { gql, useFragment, useQuery } from "@apollo/client";

// Style
// import style from "./posts-display.module.scss";

// Constants
import { postsDisplayBlock } from "@/constants/constants";
import {
  imgFields,
  catFields,
  pagFields, // REQUIRES $hasPag query var!
  slideSet,
} from "@/constants/gql-fields";

// Components
import ModuleSettings from "@/components-modules/module-settings/module-settings";
import Container from "@/components-layout/container/container";
import PostsContainer from "@/components-posts/posts-container/posts-container";
import TermsFilter from "@/components-posts/terms-filter/terms-filter";
import Pagination from "@/components-posts/pagination/pagination";

export default function PostsDisplay({ module }) {
  // Use State
  const [postsFilter, setPostsFilter] = useState();
  const [pagAfter, setPagAfter] = useState();
  const [pagBefore, setPagBefore] = useState();

  // Use Fragment
  const { complete, data } = useFragment({
    fragment: PostsDisplay.fragments.module,
    fragmentName: "PostsDisplayFragment",
    from: {
      __typename: module.__typename,
      id: module.id,
    },
  });

  if (!complete) {
    return;
  }

  // -- If Hide Module -- //
  if (data.postsDisplayBlockModuleSettings.hideModule) {
    return;
  }

  // Use Query
  const ptStyle = useQuery(GET_STYLE, {
    variables: {
      // "post" is for selected Post Items
      isPosts: data.postType == "posts" || data.postType == "post",
      // isEvents: data.postType == "events",
    },
  });

  // const ptSet = useQuery(GET_POSTTYPE_SETTINGS, {
  //   variables: {
  //     isPosts: data.postType == "posts",
  //     isEvents: data.postType == "events",
  //     isStandard: true,
  //     isList: false,
  //   },
  // });

  // if (ptStyle.loading) {
  //   return <p>loading</p>;
  // }
  // if (!ptSet.loading) {
  //   console.log(ptSet.data);
  // }

  // Variables
  const moduleSettings = data.postsDisplayBlockModuleSettings;
  const hasFilters =
    data.postDisplayOptions == "categories" ||
    data.postDisplayOptions == "filters-pagination";
  const hasPag =
    data.postDisplayOptions == "pagination" ||
    data.postDisplayOptions == "filters-pagination";

  // infiniteScroll
  // loadMore
  // slideshow

  let postData, getPosts, groupName, pagData, pagFetch;

  if (data.contentType === "post-item") {
    postData = data.postItems.nodes;
    groupName = "postGroup";
  } else if (data.contentType === "post-type") {
    const queryVars = getQueryVars(data, postsFilter);

    if (data.postType == "posts") {
      getPosts = useQuery(GET_POSTS, {
        variables: queryVars,
      });
      groupName = "postGroup";
    }
    // else if (data.postType == "events") {
    //   getPosts = useQuery(GET_EVENTS, { variables: queryVars });
    //   groupName = "eventGroup";
    // }

    if (!getPosts?.loading) {
      postData = getPosts.data?.[data.postType].nodes;
      if (hasPag) {
        pagData = getPosts.data?.[data.postType].pagination;
        pagFetch = getPosts.fetchMore;
      }
    }
    // TODO: Get gql plural name again, remove groupName var and replace with [data.postType + "Group"]
  }

  // Get Query Variables for Posts
  function getQueryVars(data, postsFilter) {
    let vars = {
      first: data.numberOfPostsShown,
    };

    // - Exclude Posts
    if (data.excludeItems == "number") {
      const skipQuery = {
        posts: SKIP_POSTS,
        // events: SKIP_EVENTS,
      };

      const skipAfter = useQuery(skipQuery[data.postType], {
        variables: { first: data.excludeNum },
      });

      if (skipAfter.loading) {
        return;
      }
      vars.after = skipAfter.data?.[data.postType].pageInfo.endCursor;
    } else if (data.excludeItems == "choose") {
      vars.skipPosts = data.excludeChosenPosts?.nodes.map((skipPost) => {
        return skipPost.id;
      });
    }

    // - Order By
    // A-Z
    if (data.orderPostsBy == "alpha") {
      vars.order = { field: "TITLE", order: "ASC" };
    }
    // Date - Newest First
    else {
      vars.order = { field: "DATE", order: "DESC" };
    }

    // Display certain categories
    if (data.contentType == "post-type" && data.posttypeCategories?.length) {
      vars.getCats = data.posttypeCategories;
      vars.operator = "IN";
    }

    // Live Filtering - overrides Posttype Categories filter
    if (hasFilters && postsFilter) {
      const allCats = postsFilter == "All";
      vars.getCats = allCats ? [] : postsFilter;
      vars.operator = allCats ? "EXISTS" : "IN";
    }

    // Pagination
    if (hasPag) {
      vars.hasPag = true;

      if (pagAfter) {
        vars.first = data.numberOfPostsShown;
        vars.after = pagAfter; //+ data.excludeNum;
        vars.last = null;
        vars.before = null;
      }
      if (pagBefore) {
        vars.last = data.numberOfPostsShown;
        vars.before = pagBefore;
        vars.after = null;
        vars.first = null;
      }
    }

    return vars;
  }

  return (
    <section id={moduleSettings.module_id} className={moduleSettings.classes}>
      <ModuleSettings settings={moduleSettings} />

      <Container>
        {hasFilters && (
          <TermsFilter postType={data.postType} setFilter={setPostsFilter} />
        )}

        {getPosts?.loading || ptStyle.loading ? (
          <PostsContainer loading numPosts={data.numberOfPostsShown} />
        ) : (
          postData &&
          ptStyle.data && (
            <PostsContainer
              posts={postData}
              postSettings={
                ptStyle.data?.postTypeSettingsOptionsPage.posttypeSettingsACF[
                  groupName
                ]
              }
              postType={data.postType}
              parent={"modules"}
            />
          )
        )}
        {hasPag && pagData && (
          <Pagination
            pagData={pagData}
            setPag={{ setPagAfter, setPagBefore }}
            type="standard"
            loading={getPosts?.loading}
          />
        )}
      </Container>
    </section>
  );
}

PostsDisplay.fragments = {
  module: gql`
    fragment PostsDisplayFragment on ${postsDisplayBlock} {
      id
      __typename

      # -- Content
      contentType
      postType
      # postItems @include(if: $isPostItems) {
      postItems  {
        nodes {
          id
          uri
          ... on Post {
            id
            title
            excerpt
            featuredImage {
              ${imgFields}
            }
            categories {
              nodes {
                ${catFields}
              }
            }
          }
          # ... on Event {
          #   id
          #   excerpt
          #   title
          #   featuredImage {
          #     ${imgFields}
          #   }
          #   eventTypes {
          #     nodes {
          #       ${catFields}
          #     }
          #   }
          # }
        }
      }
      posttypeCategories
      showWhichEvents
      excludeItems
      excludeNum
      excludeChosenPosts {
        nodes {
          id
        }
      }

      # -- Preset Module
      presetModule

      # -- Settings
      numberOfPostsShown
      postDisplayOptions
      orderPostsBy
      loadMoreText
      
      # -- Slideshow Settings
      #  ${slideSet}

      # -- Module General Settings
      postsDisplayBlockModuleSettings {
        ...ModSetFragment @unmask(mode: "migrate")
      }
    }
    ${ModuleSettings.fragments.entry}
  `,
};

const GET_STYLE = gql`
  query GET_STYLE(
    $isPosts: Boolean! = false # $isEvents: Boolean! = false
  ) {
    postTypeSettingsOptionsPage {
      id
      __typename
      posttypeSettingsACF {
        id
        __typename
        postGroup @include(if: $isPosts) {
          id
          __typename
          modulesPostStyle
          ...PostGroupFragment @unmask(mode: "migrate")
        }
        # eventGroup @include(if: $isEvents) {
        #   id
        #   __typename
        #   modulesPostStyle
        #   ...EventGroupFragment @unmask(mode: "migrate")
        # }
      }
    }
  }
  ${PostsContainer.fragments.posts}
`;
// ${PostsContainer.fragments.events}

const GET_POSTS = gql`
  query GET_POSTS(
    $order: PostObjectsConnectionOrderbyInput, 
    $first: Int,
    $skipPosts: [ID],
    $after: String,
    $last: Int,
    $before: String,
    $getCats: [String],
    $operator: TaxQueryOperator = EXISTS,
    $hasPag: Boolean! = false
  ) {
    posts( first: $first, after: $after, before: $before, last: $last, 
      where: {
        orderby: [$order],
        notIn: $skipPosts,
        # category: { name: $filterBy }
        taxQuery: { taxArray: { taxonomy: CATEGORY, field: NAME, operator: $operator, terms: $getCats } }
      },
    ) {
      nodes {
        id
        date
        author {
          node {
            id
            __typename
            name
            slug
            uri
          }
        }
        title
        uri
        excerpt
        featuredImage {
          ${imgFields}
        }
        categories {
          nodes {
            ${catFields}
          }
        }
      }
    
      # Pagination
      pagination: ${pagFields}
    }
  }
`;

const SKIP_POSTS = gql`
  query SKIP_POSTS($first: Int) {
    posts(first: $first) {
      pageInfo {
        endCursor
      }
    }
  }
`;

// const GET_EVENTS = gql`
//   query GET_EVENTS(
//     $order: PostObjectsConnectionOrderbyInput,
//     $first: Int,
//     $skipPosts: [ID],
//     $after: String,
//     $last: Int,
//     $before: String,
//     $getCats: [String],
//     $operator: TaxQueryOperator = EXISTS,
//     $hasPag: Boolean! = false
//   ) {
//     events(first: $first, after: $after, before: $before, last: $last
//       where: {
//         orderby: [$order],
//         notIn: $skipPosts,
//         taxQuery: { taxArray: { taxonomy: EVENTTYPE, field: NAME, operator: $operator, terms: $getCats } }
//       },
//     ) {
//       nodes {
//         id
//         date
//         author {
//           node {
//             id
//             __typename
//             name
//             slug
//             uri
//           }
//         }
//         title
//         uri
//         excerpt
//         featuredImage {
//           ${imgFields}
//         }
//         eventTypes {
//           nodes {
//             ${catFields}
//           }
//         }
//       }

//       # Pagination
//       pagination: ${pagFields}
//     }
//   }
// `;

// const SKIP_EVENTS = gql`
//   query SKIP_EVENTS($first: Int) {
//     events(first: $first) {
//       pageInfo {
//         endCursor
//       }
//     }
//   }
// `;
